import * as journeyStyles from '../StepCanvas/journey.module.css';
import JourneyCanvas from '../StepCanvas/JourneyCanvas';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import useMarkTemplates from '../../../api/useMarkTemplates';
import { getReachableStepIds } from '../../../hooks/useStepGraph';
import type { StepBranch } from '../../../hooks/useStepGraph';
import useDripGraphValidation from '../../../hooks/useDripGraphValidation';
import type { GraphError } from '../../../hooks/useDripGraphValidation';
import CheckSegmentCard from '../StepCanvas/CheckSegmentCard';
import ActivityCard from '../StepCanvas/ActivityCard';
import AbSplitCard from '../StepCanvas/AbSplitCard';
import AddStepMenu from './AddStepMenu';
import type { StepChoice } from './AddStepMenu';
import StepConfigPanel from './StepConfigPanel';
import DeleteStepConfirm from './DeleteStepConfirm';
import GraphErrorsPanel from './GraphErrorsPanel';
import type { DripStep, CheckSegmentStep, ActivityStep, AbSplitStep } from '../../../types';
import type { DripDraftActions } from '../../../hooks/useDripDraft';
type EdgeFieldKey = StepBranch;
const exitAddStepLabels: Record<EdgeFieldKey, string> = {
    next: '+ Add next step',
    on_true: '+ Add step if Yes',
    on_false: '+ Add step if No',
    armA: '+ Add step on A',
    armB: '+ Add step on B'
};
const getEditableTargets = (
    step: DripStep
): {
    fieldKey: EdgeFieldKey;
    target: string;
}[] => {
    switch (step.type) {
        case 'check_segment':
            return [
                { fieldKey: 'on_true', target: step.on_true },
                { fieldKey: 'on_false', target: step.on_false }
            ];
        case 'activity':
            return [{ fieldKey: 'next', target: step.next }];
        case 'ab_split':
            return [
                { fieldKey: 'armA', target: step.arms.A },
                { fieldKey: 'armB', target: step.arms.B }
            ];
    }
};
const patchTargetField = (
    step: DripStep,
    fieldKey: EdgeFieldKey,
    newTarget: string
): Partial<DripStep> => {
    switch (fieldKey) {
        case 'next':
            return { next: newTarget } satisfies Partial<ActivityStep>;
        case 'on_true':
            return { on_true: newTarget } satisfies Partial<CheckSegmentStep>;
        case 'on_false':
            return { on_false: newTarget } satisfies Partial<CheckSegmentStep>;
        case 'armA':
            return {
                arms: { A: newTarget, B: (step as AbSplitStep).arms.B }
            } satisfies Partial<AbSplitStep>;
        case 'armB':
            return {
                arms: { A: (step as AbSplitStep).arms.A, B: newTarget }
            } satisfies Partial<AbSplitStep>;
    }
};
const patchNewStepContinuation = (type: DripStep['type'], target: string): Partial<DripStep> => {
    switch (type) {
        case 'activity':
            return { next: target } satisfies Partial<ActivityStep>;
        case 'check_segment':
            return { on_true: target, on_false: target } satisfies Partial<CheckSegmentStep>;
        case 'ab_split':
            return { arms: { A: target, B: target } } satisfies Partial<AbSplitStep>;
    }
};
type Props = {
    draft: DripDraftActions;
    dripId?: number;
    draftVersionNumber?: number;
    dripTargetKind?: string;
    onEditTemplate?: (stepId: string) => void;
    onSaveAndEditTemplate?: (stepId: string) => void;
    saveAndEditTemplateDisabled?: boolean;
    sendWindowErrors?: GraphError[];
};
const StepCard: React.FC<{
    step: DripStep;
    hasLegalCycle: boolean;
    isEntry: boolean;
    isSelected: boolean;
    errorCount: number;
    onClick: () => void;
}> = ({ step, hasLegalCycle, isEntry, isSelected, errorCount, onClick }) => {
    return (
        <div
            role="button"
            className={journeyStyles.interactive}
            tabIndex={0}
            data-step-id={step.id}
            onClick={onClick}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick();
                }
            }}
            aria-pressed={isSelected}
        >
            {isEntry && <span className={journeyStyles.srOnly}>Start step</span>}
            {errorCount > 0 && (
                <span
                    className={journeyStyles.errorFlag}
                    role="img"
                    aria-label={`${errorCount} validation ${errorCount === 1 ? 'error' : 'errors'} on this step`}
                >
                    {errorCount} {errorCount === 1 ? 'issue' : 'issues'}
                </span>
            )}
            {isSelected && <span className={journeyStyles.selectedFlag}>Selected</span>}
            {step.type === 'check_segment' && (
                <CheckSegmentCard step={step} hasLegalCycle={hasLegalCycle} />
            )}
            {step.type === 'activity' && <ActivityCard step={step} />}
            {step.type === 'ab_split' && <AbSplitCard step={step} />}
        </div>
    );
};
const StepCanvasEditor: React.FC<Props> = ({
    draft,
    dripId,
    draftVersionNumber,
    dripTargetKind,
    onEditTemplate,
    onSaveAndEditTemplate,
    saveAndEditTemplateDisabled,
    sendWindowErrors
}) => {
    const { state, persistedStepIds } = draft;
    const validation = useDripGraphValidation(
        state.steps,
        state.entryStepId,
        state.sendWindowStart,
        state.sendWindowEnd
    );
    // A MarkTemplate row's channel is immutable once created. Once a step has
    // a saved template, its activity type must stay locked — otherwise the
    // step's effective channel can drift from the row's stored channel with
    // no way to reconcile them. This query follows the latest draft without
    // a version_number. Template saves must invalidate the drip_id parent
    // so both this query and pinned version lists refresh.
    const templatesQuery = useMarkTemplates({ drip_id: dripId ?? 0 }, dripId != null);
    const stepsWithTemplate = useMemo(
        () => new Set((templatesQuery.data?.data.templates ?? []).map((t) => t.step_id)),
        [templatesQuery.data]
    );
    // Fail-closed: until the query proves a persisted step has no saved
    // template (loading, error, or disabled all count as "not proven"),
    // treat it as locked. A step never sent to the server can't have a row.
    const isChannelLocked = useCallback(
        (stepId: string) => {
            if (!persistedStepIds.has(stepId)) return false;
            // A background refetch (e.g. right after a save invalidates this
            // query) keeps isSuccess true with stale data — treat it the same
            // as unproven so a step's lock doesn't blip open mid-revalidation.
            if (!templatesQuery.isSuccess || templatesQuery.isFetching) return true;
            return stepsWithTemplate.has(stepId);
        },
        [persistedStepIds, templatesQuery.isSuccess, templatesQuery.isFetching, stepsWithTemplate]
    );
    const containerRef = useRef<HTMLDivElement>(null);
    const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
    const [deleteStepId, setDeleteStepId] = useState<string | null>(null);
    const [entryGuardError, setEntryGuardError] = useState<{
        stepId: string;
        message: string;
    } | null>(null);
    const selectedStep = state.steps.find((s) => s.id === selectedStepId) ?? null;
    const focusCard = useCallback((stepId: string) => {
        requestAnimationFrame(() => {
            const el = containerRef.current?.querySelector<HTMLElement>(
                `[data-step-id="${stepId}"]`
            );
            el?.focus();
        });
    }, []);
    const handleAddStep = (choice: StepChoice) => {
        const newId = draft.addStep(choice.type, choice.activity);
        setSelectedStepId(newId);
        focusCard(newId);
    };
    const handleInsertBeforeExit = (
        sourceStep: DripStep,
        fieldKey: EdgeFieldKey,
        choice: StepChoice
    ) => {
        const sourceReason = sourceStep.exit_reason;
        const newId = draft.addStep(choice.type, choice.activity);
        const sourcePatch = patchTargetField(sourceStep, fieldKey, newId);
        const updatedSource = { ...sourceStep, ...sourcePatch } as DripStep;
        const hasRemainingExit = getEditableTargets(updatedSource).some((t) => t.target === 'exit');
        draft.updateStep(sourceStep.id, {
            ...sourcePatch,
            ...(!hasRemainingExit && { exit_reason: undefined })
        });
        draft.updateStep(newId, {
            ...patchNewStepContinuation(choice.type, 'exit'),
            ...(sourceReason !== undefined && { exit_reason: sourceReason })
        });
        setSelectedStepId(newId);
        focusCard(newId);
    };
    const handleInsertStep = (
        sourceStep: DripStep,
        fieldKey: EdgeFieldKey,
        oldTarget: string,
        choice: StepChoice
    ) => {
        const newId = draft.addStep(choice.type, choice.activity);
        draft.updateStep(sourceStep.id, patchTargetField(sourceStep, fieldKey, newId));
        draft.updateStep(newId, patchNewStepContinuation(choice.type, oldTarget));
        setSelectedStepId(newId);
        focusCard(newId);
    };
    const handleSetEntry = useCallback(
        (id: string) => {
            const reachable = getReachableStepIds(state.steps, id);
            const unreachableIds = state.steps
                .filter((s) => s.id !== id && !reachable.has(s.id))
                .map((s) => s.id);
            if (unreachableIds.length > 0) {
                setEntryGuardError({
                    stepId: id,
                    message: `Cannot set "${id}" as the entry step — it cannot reach: ${unreachableIds.join(', ')}.`
                });
                return;
            }
            setEntryGuardError(null);
            draft.setEntryStepId(id);
        },
        [state.steps, draft]
    );
    const handleDeleteRequest = (id: string) => {
        setDeleteStepId(id);
    };
    const handleClose = useCallback(() => {
        const prev = selectedStepId;
        setSelectedStepId(null);
        if (prev) focusCard(prev);
    }, [selectedStepId, focusCard]);
    const addStepRef = useRef<HTMLDivElement>(null);
    const handleDeleteConfirm = () => {
        if (deleteStepId) {
            const idx = state.steps.findIndex((s) => s.id === deleteStepId);
            draft.removeStep(deleteStepId);
            if (selectedStepId === deleteStepId) setSelectedStepId(null);
            setDeleteStepId(null);
            const remaining = state.steps.filter((s) => s.id !== deleteStepId);
            if (remaining.length === 0) {
                requestAnimationFrame(() => {
                    addStepRef.current?.querySelector<HTMLElement>('button')?.focus();
                });
            } else {
                const nearest = state.steps[idx - 1] ?? state.steps[idx + 1];
                if (nearest && nearest.id !== deleteStepId) focusCard(nearest.id);
            }
        }
    };
    const canEditTemplate = dripId != null && draftVersionNumber != null;
    if (state.steps.length === 0) {
        return (
            <div>
                <div className={journeyStyles.empty}>
                    <h3>Your journey starts here</h3>
                    <p className={journeyStyles.hint}>
                        Add an email, a segment check, or a split to begin.
                    </p>
                    <div ref={addStepRef}>
                        <AddStepMenu onAdd={handleAddStep} label="+ Add first step" />
                    </div>
                </div>
            </div>
        );
    }
    return (
        <div
            ref={containerRef}
            style={{
                display: 'flex',
                gap: '1.6rem'
            }}
        >
            <div
                style={{
                    flex: 1,
                    minWidth: 0
                }}
            >
                <JourneyCanvas
                    steps={state.steps}
                    entryStepId={state.entryStepId}
                    renderStep={(step, context) => (
                        <StepCard
                            step={step}
                            {...context}
                            isSelected={selectedStepId === step.id}
                            errorCount={validation.byStepId.get(step.id)?.length ?? 0}
                            onClick={() => setSelectedStepId(step.id)}
                        />
                    )}
                    renderBranch={(edge) => (
                        <AddStepMenu
                            compact
                            accessibleLabel={`Add step after ${edge.source.id}, ${edge.label}, before ${edge.isExit ? 'Finish' : edge.target}`}
                            label={edge.isExit ? exitAddStepLabels[edge.branch] : '+ Add step'}
                            onAdd={(choice) =>
                                edge.isExit
                                    ? handleInsertBeforeExit(edge.source, edge.branch, choice)
                                    : handleInsertStep(
                                          edge.source,
                                          edge.branch,
                                          edge.target,
                                          choice
                                      )
                            }
                        />
                    )}
                />
                <GraphErrorsPanel
                    errors={validation.errors}
                    onStepClick={(id) => setSelectedStepId(id)}
                />
            </div>
            {selectedStep && (
                <StepConfigPanel
                    step={selectedStep}
                    allSteps={state.steps}
                    entryStepId={state.entryStepId}
                    onChange={draft.updateStep}
                    onSetEntry={handleSetEntry}
                    entryError={
                        entryGuardError?.stepId === selectedStep.id ? entryGuardError.message : null
                    }
                    onDelete={handleDeleteRequest}
                    onEditTemplate={onEditTemplate}
                    onSaveAndEditTemplate={onSaveAndEditTemplate}
                    saveAndEditTemplateDisabled={saveAndEditTemplateDisabled}
                    sendWindowErrors={sendWindowErrors}
                    hasSavedTemplate={isChannelLocked(selectedStep.id)}
                    canEditTemplate={canEditTemplate}
                    persistedStepIds={persistedStepIds}
                    savedStep={draft.persistedSteps.get(selectedStep.id)}
                    dripTargetKind={dripTargetKind}
                    onClose={handleClose}
                />
            )}
            {deleteStepId && (
                <DeleteStepConfirm
                    stepId={deleteStepId}
                    allSteps={state.steps}
                    entryStepId={state.entryStepId}
                    onConfirm={handleDeleteConfirm}
                    onCancel={() => setDeleteStepId(null)}
                    onJumpToStep={(id) => {
                        setDeleteStepId(null);
                        setSelectedStepId(id);
                    }}
                />
            )}
        </div>
    );
};
export default StepCanvasEditor;
